import { useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import { SectionTitle } from '../components/SettingsRows';
import { reminderPermission, remindersSupported, requestReminderSync } from '../lib/reminders';
import { useApp } from '../lib/AppContext';

const LEADS = [{ value: 15, label: '15 phút' }, { value: 30, label: '30 phút' }, { value: 60, label: '1 giờ' }, { value: 120, label: '2 giờ' }];
const GROUP = 'divide-y divide-line rounded-[20px] bg-base-100 px-4';

function Toggle({ id, label, hint, checked, onChange, disabled }) {
  return (
    <div className="flex min-h-16 items-center justify-between gap-3 py-2">
      <label htmlFor={id} className="min-w-0">
        <span className="block text-[15px] font-semibold">{label}</span>
        {hint && <span className="block text-xs text-muted">{hint}</span>}
      </label>
      <input id={id} type="checkbox" className="toggle toggle-primary" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
    </div>
  );
}

// B5 Cài đặt → Thông báo: reminders before a session with the coach (how long before), "Nhắc nếu quên ghi"
// at 21:00, meal reminders and quiet hours. The iPhone app schedules them; the website only keeps the settings.
export default function NotificationSettingsPage() {
  const { api, reminders, setReminders } = useApp();
  const [settings, setSettings] = useState(reminders);
  const [permission, setPermission] = useState(null);
  const [error, setError] = useState(null);
  const native = remindersSupported();

  useEffect(() => { api.get('/reminders').then((data) => { setSettings(data); setReminders(data); }).catch((e) => setError(e.message)); }, [api, setReminders]);
  useEffect(() => { if (native) reminderPermission().then(setPermission).catch(() => {}); }, [native]);

  const save = async (patch) => {
    const before = settings;
    setSettings((s) => ({ ...s, ...patch }));
    setError(null);
    // Turning a reminder on is when the iPhone asks for permission.
    if (native && Object.values(patch).includes(true)) setPermission(await reminderPermission({ ask: true }).catch(() => 'denied'));
    try {
      const data = await api.patch('/reminders', patch);
      setSettings(data);
      setReminders(data);
      requestReminderSync();
    } catch (e) {
      setSettings(before);
      setError(e.message);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <PageHeader title="Thông báo" />
      {!native && <p className="mt-3 rounded-[16px] bg-[#fdf3d7] p-3 text-sm text-[#8a6100]">Nhắc nhở chỉ có trên app điện thoại. Cài đặt ở đây áp dụng cho app.</p>}
      {native && permission === 'denied' && (
        <p className="mt-3 rounded-[16px] bg-[#fdebe4] p-3 text-sm text-[#8e3414]">
          Macro Mate chưa được gửi thông báo. Mở Cài đặt của iPhone → Macro Mate → Thông báo để bật.
        </p>
      )}
      {error && <p role="alert" className="mt-3 text-sm font-medium text-error">{error}</p>}
      {!settings ? <div className="skeleton mt-4 h-60 rounded-[20px]" /> : (
        <>
          <SectionTitle>Nhắc lịch tập</SectionTitle>
          <div className={GROUP}>
            <Toggle id="r-appointments" label="Nhắc trước buổi tập" hint="Theo giờ Coach đặt cho từng buổi" checked={settings.appointments}
              onChange={(v) => save({ appointments: v })} />
            <div className="flex flex-col gap-2 py-3">
              <span className="text-xs font-semibold text-muted">Nhắc trước</span>
              <div className="grid grid-cols-4 gap-2" role="radiogroup" aria-label="Nhắc trước">
                {LEADS.map((l) => (
                  <button key={l.value} type="button" role="radio" aria-checked={settings.lead_minutes === l.value} disabled={!settings.appointments}
                    onClick={() => save({ lead_minutes: l.value })}
                    className={`h-11 rounded-[12px] border text-sm font-semibold disabled:opacity-40 ${settings.lead_minutes === l.value ? 'border-[#17804f] bg-[#e3f1e9] text-[#17804f]' : 'border-line'}`}>
                    {l.label}
                  </button>
                ))}
              </div>
            </div>
            <Toggle id="r-forgot" label="Hỏi sau buổi tập" hint="15 phút sau buổi: Đã tập hay Không tập, Coach sẽ thấy" checked={settings.forgot} onChange={(v) => save({ forgot: v })} />
          </div>
          <SectionTitle>Khác</SectionTitle>
          <div className={GROUP}>
            <Toggle id="r-meals" label="Nhắc ghi bữa ăn" hint="12:00 và 19:30, nếu bữa đó chưa ghi" checked={settings.meals} onChange={(v) => save({ meals: v })} />
            <Toggle id="r-quiet" label="Giờ yên lặng" hint={`${settings.quiet_from} – ${settings.quiet_to} · không gửi nhắc ghi bữa, quên ghi`} checked={settings.quiet}
              onChange={(v) => save({ quiet: v })} />
            {settings.quiet && (
              <div className="flex items-center gap-3 py-3 text-sm">
                <label htmlFor="r-quiet-from" className="text-muted">Từ</label>
                <input id="r-quiet-from" type="time" className="input h-11 w-28 rounded-[12px] border-line text-base" value={settings.quiet_from}
                  onChange={(e) => e.target.value && save({ quiet_from: e.target.value })} />
                <label htmlFor="r-quiet-to" className="text-muted">đến</label>
                <input id="r-quiet-to" type="time" className="input h-11 w-28 rounded-[12px] border-line text-base" value={settings.quiet_to}
                  onChange={(e) => e.target.value && save({ quiet_to: e.target.value })} />
              </div>
            )}
          </div>
          <p className="px-1 pt-3 text-xs text-muted">Nhắc trước buổi tập vẫn gửi trong giờ yên lặng, vì đó là giờ bạn đã hẹn.</p>
        </>
      )}
    </div>
  );
}
